// Push Notification Service for Mobile Messages
// Shows browser notifications when new messages arrive
// UPDATED: Supports both foreground notifications AND service worker push notifications

export class PushNotificationService {
  private static permission: NotificationPermission = 'default';
  private static isSupported = 'Notification' in window;
  private static serviceWorkerRegistration: ServiceWorkerRegistration | null = null;

  /**
   * Initialize service worker for push notifications
   * Call this on app load
   */
  static async initializeServiceWorker(): Promise<void> {
    if ('serviceWorker' in navigator) {
      try {
        this.serviceWorkerRegistration = await navigator.serviceWorker.ready;
        console.log('[PushNotifications] Service Worker ready');
      } catch (error) {
        console.error('[PushNotifications] Service Worker initialization failed:', error);
      }
    }
  }

  /**
   * Check if notifications are supported
   */
  static isNotificationSupported(): boolean {
    return this.isSupported;
  }

  /**
   * Get current notification permission status
   */
  static getPermissionStatus(): NotificationPermission {
    if (!this.isSupported) return 'denied';
    return Notification.permission;
  }

  /**
   * Request permission to show notifications
   * Call this on app load or when user first opens Messages
   */
  static async requestPermission(): Promise<NotificationPermission> {
    if (!this.isSupported) {
      console.warn('[PushNotifications] Browser does not support notifications');
      return 'denied';
    }

    if (Notification.permission === 'granted') {
      this.permission = 'granted';
      await this.initializeServiceWorker();
      return 'granted';
    }

    if (Notification.permission === 'denied') {
      this.permission = 'denied';
      return 'denied';
    }

    try {
      const permission = await Notification.requestPermission();
      this.permission = permission;
      console.log('[PushNotifications] Permission status:', permission);
      
      if (permission === 'granted') {
        await this.initializeServiceWorker();
      }
      
      return permission;
    } catch (error) {
      console.error('[PushNotifications] Error requesting permission:', error);
      return 'denied';
    }
  }

  /**
   * Show a notification via Service Worker (works even when tab is closed/locked)
   */
  static async showPersistentNotification(title: string, options?: NotificationOptions): Promise<void> {
    if (!this.serviceWorkerRegistration) {
      console.warn('[PushNotifications] Service Worker not available, falling back to regular notification');
      this.showNotification(title, options?.body || '', options?.data?.onClick);
      return;
    }

    if (Notification.permission !== 'granted') {
      console.log('[PushNotifications] Permission not granted');
      return;
    }

    try {
      await this.serviceWorkerRegistration.showNotification(title, {
        body: options?.body,
        icon: options?.icon || '/icon-192.svg',
        badge: options?.badge || '/icon-192.svg',
        tag: options?.tag || `notification-${Date.now()}`,
        requireInteraction: options?.requireInteraction || false,
        vibrate: options?.vibrate || [200, 100, 200],
        data: options?.data || {},
        actions: options?.actions || [],
        silent: options?.silent || false,
      });
      console.log('[PushNotifications] Persistent notification shown:', title);
    } catch (error) {
      console.error('[PushNotifications] Error showing persistent notification:', error);
    }
  }

  /**
   * Show a notification for a new message (PERSISTENT - works when locked)
   */
  static async showMessageNotificationPersistent(senderName: string, messagePreview: string): Promise<void> {
    await this.showPersistentNotification(`Message from: ${senderName}`, {
      body: messagePreview,
      icon: '/icon-192.svg',
      badge: '/icon-192.svg',
      tag: `message-${senderName}`,
      requireInteraction: true, // Stays until dismissed
      vibrate: [200, 100, 200],
      data: {
        type: 'message',
        sender: senderName,
        url: '/organizer/messages',
      },
      actions: [
        { action: 'open', title: 'Open Messages', icon: '/icon-192.svg' },
        { action: 'dismiss', title: 'Dismiss', icon: '/icon-192.svg' }
      ]
    });
  }

  /**
   * Show a notification for a new message (FOREGROUND - only when app is open)
   * LEGACY: Use showMessageNotificationPersistent for locked screen support
   */
  static showMessageNotification(senderName: string, messagePreview: string): void {
    if (!this.isSupported || Notification.permission !== 'granted') {
      console.log('[PushNotifications] Cannot show notification - permission:', Notification.permission);
      return;
    }

    try {
      const notification = new Notification(`Message from: ${senderName}`, {
        body: messagePreview,
        icon: '/icon-192.svg',
        badge: '/icon-192.svg',
        tag: `message-${senderName}`,
        requireInteraction: false,
        vibrate: [200, 100, 200],
        data: {
          type: 'message',
          sender: senderName,
          url: '/organizer/messages',
        },
      });

      notification.onclick = (event) => {
        event.preventDefault();
        window.focus();
        window.location.href = '/organizer/messages';
        notification.close();
      };

      // Keep notification for 15 seconds (was 5)
      setTimeout(() => notification.close(), 15000);

      console.log('[PushNotifications] Showed notification for:', senderName);
    } catch (error) {
      console.error('[PushNotifications] Error showing notification:', error);
    }
  }

  /**
   * Show a generic notification (FOREGROUND only)
   */
  static showNotification(title: string, body: string, onClick?: () => void): void {
    if (!this.isSupported || Notification.permission !== 'granted') {
      return;
    }

    try {
      const notification = new Notification(title, {
        body,
        icon: '/icon-192.svg',
        requireInteraction: false,
      });

      if (onClick) {
        notification.onclick = (event) => {
          event.preventDefault();
          onClick();
          notification.close();
        };
      }

      // Keep generic notifications for 10 seconds (was 5)
      setTimeout(() => notification.close(), 10000);
    } catch (error) {
      console.error('[PushNotifications] Error showing notification:', error);
    }
  }

  /**
   * Test notification (works when phone is locked)
   */
  static async testPushNotification(): Promise<void> {
    const permission = await this.requestPermission();
    
    if (permission !== 'granted') {
      alert('Please allow notifications in your browser settings to test push notifications.');
      return;
    }

    await this.showPersistentNotification('🔔 WHIPT Test Notification', {
      body: 'This notification should appear even when your phone is locked!',
      icon: '/icon-192.svg',
      badge: '/icon-192.svg',
      tag: 'test-notification',
      requireInteraction: true, // Won't auto-dismiss
      vibrate: [200, 100, 200, 100, 200],
      data: {
        type: 'test',
        url: '/organizer',
      },
      actions: [
        { action: 'open', title: '✅ It Works!', icon: '/icon-192.svg' },
        { action: 'dismiss', title: '❌ Dismiss', icon: '/icon-192.svg' }
      ]
    });
  }
}

